import React, { useState } from 'react';
import { jsPDF } from 'jspdf';
import { PageHero } from '../components/layout/PageHero';
import { DataTable } from '../components/procurement/DataTable';
import { Button } from '../components/ui/Button';
import { DownloadIcon } from 'lucide-react';
import { contractAwards } from '../data/procurement';
export function ContractAwards() {
  const [selectedYear, setSelectedYear] = useState('2026');
  const columns = [{
    key: 'contractRef',
    label: 'Contract Ref',
    sortable: true
  }, {
    key: 'title',
    label: 'Title',
    sortable: true
  }, {
    key: 'awardedTo',
    label: 'Awarded To',
    sortable: true
  }, {
    key: 'value',
    label: 'Value',
    sortable: true
  }, {
    key: 'awardDate',
    label: 'Award Date',
    sortable: true
  }, {
    key: 'duration',
    label: 'Duration',
    sortable: false
  }];
  const tableData = contractAwards.map(award => ({
    ...award,
    awardDate: new Date(award.awardDate).toLocaleDateString('en-GB')
  }));
  const totalValueNumber = contractAwards.reduce((sum, award) => sum + parseFloat(award.value.replace(/[£,]/g, '')), 0);
  const uniqueSuppliers = new Set(contractAwards.map(a => a.awardedTo)).size;
  const handleDownloadPDF = () => {
    const doc = new jsPDF({
      unit: 'mm',
      format: 'a4',
      orientation: 'landscape'
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 15;
    // Letterhead band
    doc.setFillColor(0, 104, 55);
    doc.rect(0, 0, pageWidth, 26, 'F');
    // Gold accent stripe
    doc.setFillColor(252, 209, 22);
    doc.rect(0, 26, pageWidth, 2, 'F');
    // AU emblem
    doc.setFillColor(255, 255, 255);
    doc.circle(marginX + 7, 13, 7, 'F');
    doc.setTextColor(0, 104, 55);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('AU', marginX + 7, 15, {
      align: 'center'
    });
    // Letterhead text
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('AFRICAN UNION COMMISSION', marginX + 20, 12);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('Procurement Department', marginX + 20, 18);
    doc.text('London Office, United Kingdom', marginX + 20, 23);
    // Document title
    doc.setTextColor(26, 31, 46);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(`CONTRACT AWARDS — ${selectedYear}`, pageWidth / 2, 42, {
      align: 'center'
    });
    doc.setDrawColor(252, 209, 22);
    doc.setLineWidth(0.8);
    doc.line(pageWidth / 2 - 35, 46, pageWidth / 2 + 35, 46);
    // Subtitle
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.text('Transparency in procurement outcomes and awarded contracts', pageWidth / 2, 52, {
      align: 'center'
    });
    // Table
    const tableStartY = 62;
    const colWidths = [32, 68, 50, 28, 30, 28];
    const headers = ['Contract Ref', 'Title', 'Awarded To', 'Value', 'Award Date', 'Duration'];
    const rowHeight = 12;
    const totalTableWidth = colWidths.reduce((a, b) => a + b, 0);
    // Header row
    let x = marginX;
    doc.setFillColor(0, 104, 55);
    doc.rect(marginX, tableStartY, totalTableWidth, 9, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    headers.forEach((h, i) => {
      doc.text(h, x + 2, tableStartY + 6);
      x += colWidths[i];
    });
    // Body rows
    let y = tableStartY + 9;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    contractAwards.forEach((award, rowIndex) => {
      // Zebra stripe
      if (rowIndex % 2 === 0) {
        doc.setFillColor(250, 247, 242);
      } else {
        doc.setFillColor(255, 255, 255);
      }
      doc.rect(marginX, y, totalTableWidth, rowHeight, 'F');
      doc.setTextColor(26, 31, 46);
      x = marginX;
      const values = [award.contractRef, award.title, award.awardedTo, award.value, new Date(award.awardDate).toLocaleDateString('en-GB'), award.duration];
      values.forEach((val, i) => {
        const wrapped = doc.splitTextToSize(String(val), colWidths[i] - 4);
        // Bold the contract ref and value
        if (i === 0 || i === 3) {
          doc.setFont('helvetica', 'bold');
          if (i === 3) doc.setTextColor(0, 104, 55);
        } else {
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(26, 31, 46);
        }
        doc.text(wrapped.slice(0, 2), x + 2, y + 5);
        x += colWidths[i];
      });
      y += rowHeight;
    });
    // Table border
    doc.setDrawColor(229, 231, 235);
    doc.setLineWidth(0.2);
    doc.rect(marginX, tableStartY, totalTableWidth, y - tableStartY);
    // Summary block — three stat cards
    const summaryY = y + 8;
    const cardWidth = (totalTableWidth - 8) / 3;
    const cardHeight = 22;
    // Card 1: Total contracts
    doc.setFillColor(232, 243, 237);
    doc.roundedRect(marginX, summaryY, cardWidth, cardHeight, 2, 2, 'F');
    doc.setTextColor(0, 104, 55);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text(String(contractAwards.length), marginX + cardWidth / 2, summaryY + 11, {
      align: 'center'
    });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(80, 80, 80);
    doc.text('Total Contracts Awarded', marginX + cardWidth / 2, summaryY + 17, {
      align: 'center'
    });
    // Card 2: Total value
    doc.setFillColor(232, 243, 237);
    doc.roundedRect(marginX + cardWidth + 4, summaryY, cardWidth, cardHeight, 2, 2, 'F');
    doc.setTextColor(0, 104, 55);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text(`£${(totalValueNumber / 1000000).toFixed(2)}M`, marginX + cardWidth * 1.5 + 4, summaryY + 11, {
      align: 'center'
    });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(80, 80, 80);
    doc.text('Total Contract Value', marginX + cardWidth * 1.5 + 4, summaryY + 17, {
      align: 'center'
    });
    // Card 3: Unique suppliers
    doc.setFillColor(232, 243, 237);
    doc.roundedRect(marginX + (cardWidth + 4) * 2, summaryY, cardWidth, cardHeight, 2, 2, 'F');
    doc.setTextColor(0, 104, 55);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text(String(uniqueSuppliers), marginX + cardWidth * 2.5 + 8, summaryY + 11, {
      align: 'center'
    });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(80, 80, 80);
    doc.text('Unique Suppliers', marginX + cardWidth * 2.5 + 8, summaryY + 17, {
      align: 'center'
    });
    // Footer
    const footerY = pageHeight - 18;
    doc.setDrawColor(229, 231, 235);
    doc.setLineWidth(0.3);
    doc.line(marginX, footerY, pageWidth - marginX, footerY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(80, 80, 80);
    doc.text('Procurement Department  |  procurement@aucommission.org  |  +44 (0) 20 4571 0123', pageWidth / 2, footerY + 5, {
      align: 'center'
    });
    doc.text('African Union Commission, London Office  |  www.africanunion.org', pageWidth / 2, footerY + 10, {
      align: 'center'
    });
    doc.setTextColor(150, 150, 150);
    doc.text(`Officially issued document  |  Generated: ${new Date().toLocaleDateString('en-GB')}`, pageWidth / 2, footerY + 14, {
      align: 'center'
    });
    doc.save(`AU-Contract-Awards-${selectedYear}.pdf`);
  };
  return <div data-id="element-726">
      <PageHero title="Contract Awards" subtitle="Recently awarded contracts and procurement outcomes" breadcrumbs={[{
      label: 'Home',
      path: '/'
    }, {
      label: 'Procurement'
    }, {
      label: 'Contract Awards'
    }]} data-id="element-727" />

      <div className="max-w-7xl mx-auto px-4 py-12" data-id="element-728">
        <div className="bg-white rounded-lg border border-gray-border p-6 mb-8" data-id="element-729">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4" data-id="element-730">
            <div data-id="element-731">
              <h2 className="text-2xl font-bold text-charcoal mb-2" data-id="element-732">
                Contract Awards {selectedYear}
              </h2>
              <p className="text-gray-600" data-id="element-733">
                Transparency in procurement outcomes and contract awards
              </p>
            </div>
            <div className="flex items-center gap-4" data-id="element-734">
              <select value={selectedYear} onChange={e => setSelectedYear(e.target.value)} className="px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-735">
                <option value="2026" data-id="element-736">2026</option>
                <option value="2025" data-id="element-737">2025</option>
                <option value="2024" data-id="element-738">2024</option>
              </select>
              <Button variant="secondary" onClick={handleDownloadPDF} className="flex items-center gap-2" data-id="element-739">
                <DownloadIcon size={20} data-id="element-740" />
                Download PDF
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-border overflow-hidden" data-id="element-741">
          <DataTable columns={columns} data={tableData} data-id="element-742" />
        </div>

        <div className="mt-8 grid md:grid-cols-3 gap-6" data-id="element-743">
          <div className="bg-white rounded-lg border border-gray-border p-6" data-id="element-744">
            <div className="text-3xl font-bold text-au-green mb-2" data-id="element-745">
              {contractAwards.length}
            </div>
            <div className="text-gray-600" data-id="element-746">Total Contracts Awarded</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-border p-6" data-id="element-747">
            <div className="text-3xl font-bold text-au-green mb-2" data-id="element-748">
              £{(totalValueNumber / 1000000).toFixed(1)}M
            </div>
            <div className="text-gray-600" data-id="element-749">Total Contract Value</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-border p-6" data-id="element-750">
            <div className="text-3xl font-bold text-au-green mb-2" data-id="element-751">
              {uniqueSuppliers}
            </div>
            <div className="text-gray-600" data-id="element-752">Unique Suppliers</div>
          </div>
        </div>
      </div>
    </div>;
}